import React, { useRef, useLayoutEffect } from "react"
import styled from "styled-components"
import { Helmet } from "react-helmet"

import { PromotionBar, ImpersonationBar, Header } from "components/navigation"
import StepNav from "components/navigation/step-nav"
import { ParagraphSmall } from "components/text"
import { Flex, Box } from "components/box"

import { useStepNavSteps } from "./useStepNavSteps"
import { usePromobar } from "shared/context/promobar"

import { home as page } from "pages/meta.js"
import { CurrentUser } from "graphql/smart-queries/user"

import appConfig, { isDevEnvironment, isStagingEnvironment } from "config/app"
import { useNavigationLayout } from "shared/context/navigation-layout"

const NavHeader = styled(Box)`
  position: fixed;
  width: 100%;
  z-index: ${props => props.theme.layers.navigation};
  background-color: ${props => props.theme.colors.white};
`

const Container = styled(Flex).attrs({
  flexDirection: "column",
  justifyContent: "stretch",
  overflowX: "hidden"
})``

type NavigationLayoutProps = {
  currentUser: Maybe<CurrentUser>
  showOfferBanner: boolean
}

const NavigationLayout: React.FC<NavigationLayoutProps> = ({ children, currentUser, showOfferBanner }) => {
  const stepNavSteps = useStepNavSteps()
  const { setNavigationLayoutOffsetHeight } = useNavigationLayout()
  const { promobarContent, promobarHref } = usePromobar()

  const DISPLAY_APP_VERSION = isStagingEnvironment || isDevEnvironment

  const isLoggedIn = currentUser?.active
  const fullName = currentUser?.fullName ?? ""

  /*
   * On react-snap prerender the offer banner is renderd with default offer.
   * However on first render cycle it's not rendered because we have the loading guard.
   * This conflict results in mismatches in prerendered and first-cycle rendered HTML and incorrect styling.
   *
   * Later we use this check to not render the offer banner on the prerender.
   */
  const ref = useRef<HTMLDivElement>(null)

  useLayoutEffect(() => {
    if (ref.current?.offsetHeight) {
      setNavigationLayoutOffsetHeight(ref.current?.offsetHeight)
    }
  }, [ref.current?.offsetHeight])

  return (
    <>
      <Helmet>
        <title>{page.title}</title>
        <meta name="title" content={page.title} />
        <meta name="description" content={page.description} />
        <meta name="og:title" content={page["og:title"]} />
        <meta name="og:description" content={page["og:description"]} />
      </Helmet>
      <Container>
        <NavHeader ref={ref}>
          {currentUser?.isImpersonated && <ImpersonationBar currentUser={currentUser} />}
          {showOfferBanner && promobarContent !== null && promobarContent !== undefined && (
            /* If the promobar needs to be animated see the code removed in these PRs:
              https://github.com/ThirtyMadison/cove/pull/2116
              https://github.com/ThirtyMadison/cove/pull/2859
             */
            <PromotionBar text={promobarContent} href={promobarHref} />
          )}
          <Header isLoggedIn={isLoggedIn} fullName={fullName} />
          {stepNavSteps && <StepNav steps={stepNavSteps} />}
          {DISPLAY_APP_VERSION && <ParagraphSmall textAlign="center">{appConfig.version}</ParagraphSmall>}
        </NavHeader>
        <Box minHeight="100vh" paddingTop={ref.current?.clientHeight}>
          {children}
        </Box>
      </Container>
    </>
  )
}

export default NavigationLayout
